import classNames from 'classnames';
import { useIntl } from 'react-intl';
import ConditionAlert from '@alltrails/conditions/components/ConditionAlert';
import ShimmerContainer from '@alltrails/core/components/ShimmerContainer';
import WeatherIcon from '@alltrails/denali/components/WeatherIcon';
import Sunrise from '@alltrails/denali/icons/Sunrise';
import Sunset from '@alltrails/denali/icons/Sunset';
import Thermometer from '@alltrails/denali/icons/Thermometer';
import Weather12 from '@alltrails/denali/icons/Weather12';
import { Day, SelectedDay } from './dayTypes';
import styles from './MobileDailySummaries.module.scss';

type MobileDailySummariesProps = {
  days: (Day | SelectedDay)[];
  onChange: (utcMs: number, index: number) => void;
  selectedUTCMs: number;
};

const MobileDailySummaries = ({ days, onChange, selectedUTCMs }: MobileDailySummariesProps) => {
  const intl = useIntl();

  const formatTemp = (temp: number) => intl.formatNumber(temp, { style: 'unit', unit: 'degree', unitDisplay: 'narrow', maximumFractionDigits: 0 });

  const renderSelectedSummary = ({
    alert,
    precipitationChance,
    sunrise,
    sunset,
    summary,
    tempCurrent,
    tempHigh,
    tempLow,
    weatherIconCode
  }: SelectedDay) => {
    return (
      <div className={styles.dailySummary}>
        <div className={styles.iconAndSummary}>
          <WeatherIcon colored={false} iconCode={weatherIconCode} />
          {summary}
        </div>
        <div className={styles.selectedTemps}>
          {tempCurrent !== undefined ? (
            <div className={styles.selectedTemp}>{formatTemp(tempCurrent)}</div>
          ) : (
            <>
              <div className={styles.selectedTemp}>{formatTemp(tempHigh)}</div>
              <div className={styles.selectedTemp}>{formatTemp(tempLow)}</div>
            </>
          )}
        </div>
        <div className={styles.statsContainer}>
          <div className={styles.stats}>
            {tempCurrent !== undefined ? (
              <>
                <div className={styles.stat}>
                  <Thermometer color="currentColor" size="xs" />
                  <span>
                    {formatTemp(tempHigh)}
                    <span className={styles.secondary}> / </span>
                    {formatTemp(tempLow)}
                  </span>
                </div>
              </>
            ) : null}
            {precipitationChance !== undefined ? (
              <div className={styles.stat}>
                <Weather12 color="currentColor" size="xs" />
                {intl.formatNumber(Math.round(precipitationChance), { style: 'unit', unit: 'percent' })}
              </div>
            ) : null}
          </div>
          <div className={styles.stats}>
            {sunrise ? (
              <div className={classNames(styles.stat, styles.lowercase)}>
                <Sunrise color="currentColor" size="xs" />
                {intl.formatTime(sunrise, { timeZone: 'UTC' })}
              </div>
            ) : null}
            {sunset ? (
              <div className={classNames(styles.stat, styles.lowercase)}>
                <Sunset color="currentColor" size="xs" />
                {intl.formatTime(sunset, { timeZone: 'UTC' })}
              </div>
            ) : null}
          </div>
        </div>
        {alert ? <ConditionAlert className={styles.alert} {...alert} /> : null}
      </div>
    );
  };

  const selectedDay = days.find(day => 'summary' in day);

  return (
    <div className={styles.container}>
      <div className={styles.buttons}>
        {days.map((day, index) => {
          const { utcMs, weatherIconCode } = day;
          const isSelected = selectedUTCMs === utcMs;
          const weekdayLong = intl.formatDate(new Date(utcMs), { weekday: 'long', timeZone: 'UTC' });
          const weekdayShort = intl.formatDate(new Date(utcMs), { weekday: 'short', timeZone: 'UTC' });
          return (
            <button
              key={utcMs}
              aria-label={weekdayLong}
              className={classNames(styles.dailySummaryButton, { [styles.selected]: isSelected })}
              onClick={!isSelected ? () => onChange(utcMs, index) : undefined}
            >
              <span>{weekdayShort}</span>
              <WeatherIcon colored iconCode={weatherIconCode} />
            </button>
          );
        })}
      </div>
      {selectedDay ? renderSelectedSummary(selectedDay) : <DailySummaryLoading renderShimmerContainer />}
    </div>
  );
};

export default MobileDailySummaries;

const DailySummaryLoading = ({ renderShimmerContainer }: { renderShimmerContainer: boolean }) => {
  const contents = (
    <>
      <div className={classNames(styles.iconAndSummary, styles.loading)}>&nbsp;</div>
      <div className={styles.selectedTemps}>
        <div className={classNames(styles.selectedTemp, styles.loading)}>&nbsp;</div>
      </div>
      <div className={styles.statsContainer}>
        <div className={styles.stats}>
          <div className={classNames(styles.stat, styles.loading)}>&nbsp;</div>
        </div>
      </div>
    </>
  );

  return renderShimmerContainer ? (
    <ShimmerContainer className={styles.dailySummary}>{contents}</ShimmerContainer>
  ) : (
    <div className={styles.dailySummary}>{contents}</div>
  );
};

export const MobileDailySummariesLoading = () => {
  return (
    <ShimmerContainer className={styles.container}>
      <div className={styles.buttons}>
        {[...Array(7)].map((_, i) => (
          <div key={`daily-summary-${i}`} className={classNames(styles.dailySummaryButton, styles.loading)} />
        ))}
      </div>
      <DailySummaryLoading renderShimmerContainer={false} />
    </ShimmerContainer>
  );
};
